"use client";

import { useState } from "react";

/**
 * The Lab's charts. Interactive and animated, with three rules.
 *
 * 1. They animate from a VISIBLE resting state, never from opacity 0, because the
 *    first frame a reader or a link preview gets has to be readable.
 * 2. prefers-reduced-motion skips straight to the final state.
 * 3. Hover and keyboard focus both work, and the hit target spans the whole
 *    band rather than the bar, so a 2px bar is still reachable.
 */

/**
 * The entrance animation is CSS, not requestAnimationFrame.
 *
 * The first version drove it from rAF with a progress value starting at 0,
 * which meant a chart whose bars had zero height until frames arrived, and
 * where rAF is throttled or unavailable they never did, leaving the chart
 * blank. Marks now always render at their FINAL geometry and the animation
 * only scales them up from a still-readable 0.72, with `both` fill mode, so if
 * the animation never runs the chart is simply correct and static.
 *
 * `grow-y` / `grow-x` are defined in app/globals.css alongside the
 * prefers-reduced-motion guard.
 */

type Pt = { label: string; value: number; sub?: string };

/**
 * Column chart with a fitted-trend reference line. Used for the hero series.
 * `trendFrom`/`trendTo` draw the fitted line, so the reader sees the trend the
 * regression found rather than being asked to eyeball it.
 */
export function GrowthColumns({
  points,
  unit = "",
  trendFrom,
  trendTo,
  caption,
}: {
  points: Pt[];
  unit?: string;
  trendFrom?: number;
  trendTo?: number;
  caption?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const W = 440;
  const H = 168;
  const base = 138;
  const top = 16;
  const max = Math.max(...points.map((p) => p.value), trendTo ?? 0) * 1.12;
  const span = W / points.length;
  const barW = Math.min(34, span * 0.56);
  const y = (v: number) => base - (v / max) * (base - top);

  const active = hover === null ? points.length - 1 : hover;

  return (
    <div className="flex flex-col gap-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full touch-manipulation"
        role="img"
        aria-label={`Column chart. ${points
          .map((p) => `${p.label} ${p.value}${unit}`)
          .join(", ")}.`}
        onMouseLeave={() => setHover(null)}
      >
        <line
          x1="0"
          x2={W}
          y1={base}
          y2={base}
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1"
        />

        {points.map((p, i) => {
          const cx = span * i + span / 2;
          const full = y(p.value);
          const h = base - full;
          const on = active === i;
          return (
            <g
              key={p.label}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              tabIndex={0}
              role="button"
              aria-label={`${p.label}: ${p.value}${unit}`}
              className="cursor-default outline-none [&:focus-visible>rect.hit]:stroke-[#EEF975]"
            >
              {/* Full-height hit target: a short bar is still hoverable. */}
              <rect
                className="hit"
                x={cx - span / 2}
                y={0}
                width={span}
                height={base}
                fill="transparent"
                strokeWidth="1"
              />
              <rect
                className="lab-grow-y"
                x={cx - barW / 2}
                y={base - h}
                width={barW}
                height={Math.max(h, 0)}
                fill={on ? "rgba(57,135,229,0.62)" : "rgba(57,135,229,0.42)"}
                style={{
                  transition: "fill .12s",
                  transformOrigin: `${cx}px ${base}px`,
                  animationDelay: `${i * 45}ms`,
                }}
              />
              {/* Only on a real hover. At rest the value lives in the
                  caption row: a permanent accent-coloured number floating over
                  the tallest bar read as decoration rather than as data. */}
              {hover === i && (
                <text
                  x={cx}
                  y={Math.max(base - h - 7, 11)}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.85)"
                  fontSize="10.5"
                  className="font-mono"
                >
                  {p.value}
                  {unit}
                </text>
              )}
              <text
                x={cx}
                y={H - 5}
                textAnchor="middle"
                fill={on ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.34)"}
                fontSize="10"
                className="font-mono"
              >
                {p.label}
              </text>
            </g>
          );
        })}

        {/* The fitted trend, drawn last so it sits above the bars. A hairline
            in plain white, not the accent: at 1.5px in signal yellow it was
            the loudest thing on the page and read as a drawn-on arrow rather
            than as a regression. The accent is spent once, on the rate. */}
        {trendFrom !== undefined && trendTo !== undefined && (
          <>
            <line
              x1={span / 2}
              x2={span * (points.length - 0.5)}
              y1={y(trendFrom)}
              y2={y(trendTo)}
              stroke={
                hover === null
                  ? "rgba(255,255,255,0.46)"
                  : "rgba(255,255,255,0.82)"
              }
              strokeWidth="1"
              strokeLinecap="square"
              style={{ transition: "stroke .12s" }}
            />
            {/* The highlight rides the trend line, not the bars: the line is
                the claim the chart is making, so hovering a month should show
                you where that month sits ON it. */}
            {hover !== null && (
              <circle
                cx={span * hover + span / 2}
                cy={
                  y(trendFrom) +
                  ((y(trendTo) - y(trendFrom)) * hover) / (points.length - 1)
                }
                r="3"
                fill="#EEF975"
              />
            )}
          </>
        )}
      </svg>

      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        {caption && (
          <span className="font-mono text-[11px] leading-[1.5] text-white/[.34]">
            {caption}
          </span>
        )}
        <span className="font-mono text-[11px] tabular-nums text-white/50">
          {points[active].label}: {points[active].value}
          {unit}
          {points[active].sub ? ` · ${points[active].sub}` : ""}
        </span>
      </div>
    </div>
  );
}

/**
 * Stacked share bar. Every band is drawn with a visible fill and a hairline
 * separator, because the earlier version rendered the "untouched" remainder at
 * 7% white and it read as empty space rather than as data.
 */
export function ShareBar({
  bands,
  height = 26,
}: {
  bands: { label: string; pct: number; tone?: "lead" | "mid" | "rest" }[];
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const fill = (tone: string | undefined, on: boolean) => {
    if (tone === "lead") return on ? "#5A9BEF" : "#3987E5";
    if (tone === "mid")
      return on ? "rgba(57,135,229,0.68)" : "rgba(57,135,229,0.55)";
    // "rest" must be clearly a band, not background.
    return on ? "rgba(255,255,255,0.30)" : "rgba(255,255,255,0.20)";
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div
        className="lab-grow-x flex w-full overflow-hidden"
        style={{ height }}
        onMouseLeave={() => setHover(null)}
      >
        {bands.map((b, i) => (
          <div
            key={b.label}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            tabIndex={0}
            role="button"
            aria-label={`${b.label}: ${b.pct.toFixed(1)}%`}
            title={`${b.label}: ${b.pct.toFixed(1)}%`}
            className="h-full outline-none focus-visible:ring-1 focus-visible:ring-[#EEF975]"
            style={{
              width: `${b.pct}%`,
              background: fill(b.tone, hover === i),
              borderRight:
                i < bands.length - 1 ? "1px solid #0B0C0E" : undefined,
              transition: "background .12s",
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {bands.map((b, i) => (
          <span
            key={b.label}
            className={`flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.1em] transition-colors ${
              hover === i ? "text-white" : "text-white/[.42]"
            }`}
          >
            <span
              className="block h-2 w-2 shrink-0"
              style={{ background: fill(b.tone, false) }}
              aria-hidden="true"
            />
            {b.label} {b.pct.toFixed(1)}%
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Horizontal ranked bars with an animated grow-in. Used for crawler reach and
 * for the leaderboard on the hub.
 */
export function RankedBars({
  rows,
  unit = "%",
  max,
}: {
  rows: { label: string; sub?: string; value: number; lead?: boolean }[];
  unit?: string;
  max?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const top = max ?? Math.max(...rows.map((r) => Math.abs(r.value)));

  return (
    <div className="flex flex-col" onMouseLeave={() => setHover(null)}>
      {rows.map((r, i) => (
        <div
          key={r.label}
          onMouseEnter={() => setHover(i)}
          onFocus={() => setHover(i)}
          onBlur={() => setHover(null)}
          tabIndex={0}
          role="button"
          aria-label={`${r.label}: ${r.value}${unit}`}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 border-t border-white/[.07] py-2.5 outline-none first:border-t-0 focus-visible:bg-white/[.03] lg:grid-cols-[176px_minmax(0,1fr)_58px]"
        >
          <div className="flex min-w-0 items-baseline gap-2">
            <span
              className={`truncate font-mono text-[13px] transition-colors ${
                hover === i ? "text-white" : "text-white/85"
              }`}
            >
              {r.label}
            </span>
            {r.sub && (
              <span className="hidden shrink-0 font-sans text-[12px] text-white/[.38] lg:inline">
                {r.sub}
              </span>
            )}
          </div>
          <div className="order-3 col-span-2 h-[5px] bg-white/[.07] lg:order-none lg:col-span-1">
            <div
              className="lab-grow-x h-full"
              style={{
                animationDelay: `${i * 35}ms`,
                width: `${(Math.abs(r.value) / top) * 100}%`,
                background: r.lead
                  ? hover === i
                    ? "#5A9BEF"
                    : "#3987E5"
                  : hover === i
                    ? "rgba(57,135,229,0.7)"
                    : "rgba(57,135,229,0.45)",
                transition: "background .12s",
              }}
            />
          </div>
          <span
            className={`justify-self-end font-mono text-[12.5px] tabular-nums transition-colors ${
              hover === i ? "text-white" : "text-white/60"
            }`}
          >
            {r.value}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Distribution columns with a boundary marker. Used for the page-age trap: the
 * marker is where a 28-day pre-window ends, and everything to its left is a
 * page whose "before" period predates the page.
 *
 * Bars inside the boundary carry the signal fill and bars outside are muted,
 * because the point of the chart is the share on the wrong side of the line,
 * not the shape of the tail.
 */
export function DistributionColumns({
  bars,
  boundaryAfter,
  boundaryLabel,
  insideLabel,
}: {
  bars: { label: string; pct: number; inside: boolean }[];
  boundaryAfter: number;
  boundaryLabel: string;
  insideLabel?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const W = 460;
  const H = 190;
  const base = 150;
  const top = 22;
  const max = Math.max(...bars.map((b) => b.pct)) * 1.14;
  const span = W / bars.length;
  const barW = Math.min(44, span * 0.62);
  const y = (v: number) => base - (v / max) * (base - top);
  const boundaryX = span * (boundaryAfter + 1);

  return (
    <div className="flex flex-col gap-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full touch-manipulation"
        role="img"
        aria-label={`Distribution. ${bars
          .map((b) => `${b.label} ${b.pct}%`)
          .join(", ")}. ${boundaryLabel}.`}
        onMouseLeave={() => setHover(null)}
      >
        <line
          x1="0"
          x2={W}
          y1={base}
          y2={base}
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1"
        />

        {/* The boundary, drawn under the bars. */}
        <line
          x1={boundaryX}
          x2={boundaryX}
          y1={top - 12}
          y2={base}
          stroke="#EEF975"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
        <text
          x={boundaryX - 6}
          y={top - 14}
          textAnchor="end"
          fill="#EEF975"
          fontSize="9.5"
          className="font-mono"
        >
          {boundaryLabel}
        </text>

        {bars.map((b, i) => {
          const cx = span * i + span / 2;
          const h = base - y(b.pct);
          const on = hover === i;
          return (
            <g
              key={b.label}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              tabIndex={0}
              role="button"
              aria-label={`${b.label}: ${b.pct}%`}
              className="cursor-default outline-none [&:focus-visible>rect.hit]:stroke-[#EEF975]"
            >
              <rect
                className="hit"
                x={cx - span / 2}
                y={0}
                width={span}
                height={base}
                fill="transparent"
                strokeWidth="1"
              />
              <rect
                className="lab-grow-y"
                x={cx - barW / 2}
                y={base - h}
                width={barW}
                height={Math.max(h, 0)}
                fill={
                  b.inside
                    ? on
                      ? "#5A9BEF"
                      : "#3987E5"
                    : on
                      ? "rgba(255,255,255,0.30)"
                      : "rgba(255,255,255,0.18)"
                }
                style={{
                  transition: "fill .12s",
                  transformOrigin: `${cx}px ${base}px`,
                  animationDelay: `${i * 45}ms`,
                }}
              />
              <text
                x={cx}
                y={base - h - 7}
                textAnchor="middle"
                fill={on ? "#EEF975" : "rgba(255,255,255,0.55)"}
                fontSize="10.5"
                className="font-mono"
              >
                {b.pct}%
              </text>
              <text
                x={cx}
                y={H - 5}
                textAnchor="middle"
                fill={on ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.34)"}
                fontSize="10"
                className="font-mono"
              >
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>

      {insideLabel && (
        <span className="font-mono text-[11px] leading-[1.5] text-white/[.34]">
          {insideLabel}
        </span>
      )}
    </div>
  );
}

/**
 * Interval rows: a point estimate with its 95% interval, on an axis centred on
 * zero. Used for the optimization leaderboard.
 *
 * Centred on zero and drawn with the interval visible because that is the
 * whole content of the chart: a row whose interval crosses the zero line has
 * not been separated from zero, and a bar chart would hide exactly that. The
 * accent marks only the rows that clear it.
 */
export function IntervalRows({
  rows,
  scale = 1.6,
  unit = "pp",
}: {
  rows: {
    label: string;
    sub?: string;
    value: number;
    ciHalf: number;
    /** Relative lift against the row's own baseline, in percent. Rendered in
     *  place of the percentage-point figure, which reads as nothing at a 2.9%
     *  base rate. */
    relPct?: number;
    detected?: boolean;
  }[];
  scale?: number;
  unit?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const pos = (v: number) =>
    50 + (Math.max(-scale, Math.min(scale, v)) / scale) * 50;

  return (
    <div className="flex min-w-0 flex-col" onMouseLeave={() => setHover(null)}>
      <div className="mb-2 grid grid-cols-[minmax(0,1fr)_92px] items-baseline gap-x-4 lg:grid-cols-[188px_minmax(0,1fr)_92px]">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/40">
          Change
        </span>
        <div className="relative hidden lg:block">
          <span className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/40">
            &minus;{scale} &#124; 0 &#124; +{scale}
          </span>
        </div>
        <span className="text-right font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/40">
          Lift
        </span>
      </div>

      {rows.map((r, i) => {
        const on = hover === i;
        const lo = pos(r.value - r.ciHalf);
        const hi = pos(r.value + r.ciHalf);
        const mid = pos(r.value);
        const tone = r.detected
          ? on
            ? "#EEF975"
            : "#EEF975"
          : on
            ? "rgba(255,255,255,0.72)"
            : "rgba(255,255,255,0.5)";
        return (
          <div
            key={r.label}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            tabIndex={0}
            role="button"
            aria-label={
              r.detected
                ? `${r.label}: ${r.relPct !== undefined ? `${r.relPct}% more often` : `${r.value}${unit}`}, 95% interval ${(r.value - r.ciHalf).toFixed(2)} to ${(r.value + r.ciHalf).toFixed(2)} ${unit}`
                : `${r.label}: no difference detected, 95% interval ${(r.value - r.ciHalf).toFixed(2)} to ${(r.value + r.ciHalf).toFixed(2)} ${unit}`
            }
            className="grid grid-cols-[minmax(0,1fr)_92px] items-center gap-x-4 gap-y-2 border-t border-white/[.07] py-3.5 outline-none last:border-b focus-visible:bg-white/[.03] lg:grid-cols-[188px_minmax(0,1fr)_92px]"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span
                className={`truncate font-sans text-[14px] transition-colors ${
                  on ? "text-white" : "text-white/85"
                }`}
              >
                {r.label}
              </span>
              {r.sub && (
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-white/[.34]">
                  {r.sub}
                </span>
              )}
            </div>

            <div className="relative order-3 col-span-2 h-[22px] lg:order-none lg:col-span-1">
              <div className="absolute inset-y-0 left-1/2 w-px bg-white/[.22]" />
              {/* the interval */}
              <div
                className="lab-grow-x absolute top-1/2 h-[3px] -translate-y-1/2"
                style={{
                  left: `${lo}%`,
                  width: `${Math.max(hi - lo, 0.5)}%`,
                  background: r.detected
                    ? "rgba(238,249,117,0.32)"
                    : "rgba(255,255,255,0.16)",
                  animationDelay: `${i * 40}ms`,
                }}
              />
              {/* The point estimate, drawn ONLY where the interval clears
                  zero. On a row this design could not locate, a mark sitting
                  to one side of the centre line asserts a direction the data
                  does not support, and a reader takes the mark as the answer.
                  Those rows get their interval and nothing else. */}
              {r.detected && (
                <div
                  className="absolute top-1/2 h-[13px] w-[2px] -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: `${mid}%`,
                    background: tone,
                    transition: "background .12s",
                  }}
                />
              )}
            </div>

            <span
              className={`justify-self-end text-right font-mono tabular-nums transition-colors ${
                r.detected
                  ? `text-[12.5px] ${on ? "text-white" : "text-white/[.85]"}`
                  : `text-[10px] uppercase tracking-[0.1em] ${on ? "text-white/60" : "text-white/[.34]"}`
              }`}
            >
              {r.detected && r.relPct !== undefined
                ? `+${r.relPct}%`
                : "no difference"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Win-rate bars: the share of sites where a change came out ahead, against a
 * 50% no-effect line.
 *
 * The line at 50 is the whole point of the chart, so it is drawn over the bars
 * rather than under them. A change that does nothing lands on it, which is
 * what llms.txt does, and that reads correctly without any special casing.
 *
 * Deliberately not a magnitude chart. A win rate says how often a change is
 * ahead and never by how much, and the axis label says so.
 */
export function WinRateBars({
  rows,
}: {
  rows: {
    label: string;
    sub?: string;
    pct: number;
    detected?: boolean;
    scope?: "page" | "site";
  }[];
}) {
  const [hover, setHover] = useState<number | null>(null);

  return (
    <div className="flex min-w-0 flex-col" onMouseLeave={() => setHover(null)}>
      <div className="mb-2 grid grid-cols-[minmax(0,1fr)_66px] items-baseline gap-x-4 lg:grid-cols-[200px_minmax(0,1fr)_66px]">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/40">
          Change
        </span>
        <div className="relative hidden lg:block">
          <span
            className="absolute -translate-x-1/2 whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/[.34]"
            style={{ left: "50%" }}
          >
            no effect
          </span>
        </div>
        <span className="text-right font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/40">
          Sites won
        </span>
      </div>

      {rows.map((r, i) => {
        const on = hover === i;
        const tone = r.detected
          ? on
            ? "#EEF975"
            : "rgba(238,249,117,0.78)"
          : "rgba(255,255,255,0.22)";
        return (
          <div
            key={r.label}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            tabIndex={0}
            role="button"
            aria-label={
              r.detected
                ? `${r.label}: ahead on ${r.pct}% of sites`
                : `${r.label}: no effect detected`
            }
            className="grid grid-cols-[minmax(0,1fr)_66px] items-center gap-x-4 gap-y-2 border-t border-white/[.07] py-3.5 outline-none last:border-b focus-visible:bg-white/[.03] lg:grid-cols-[200px_minmax(0,1fr)_66px]"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span
                className={`truncate font-sans text-[14px] transition-colors ${
                  on ? "text-white" : "text-white/85"
                }`}
              >
                {r.label}
              </span>
              {r.sub && (
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-white/[.34]">
                  {r.sub}
                </span>
              )}
            </div>

            <div className="relative order-3 col-span-2 h-[16px] lg:order-none lg:col-span-1">
              <div className="absolute inset-x-0 top-1/2 h-[10px] -translate-y-1/2 bg-white/[.05]" />
              <div
                className="lab-grow-x absolute top-1/2 h-[10px] -translate-y-1/2"
                style={{
                  left: 0,
                  width: `${r.pct}%`,
                  background: tone,
                  transition: "background .12s",
                  animationDelay: `${i * 40}ms`,
                }}
              />
              {/* The no-effect line, over the bars. */}
              <div className="absolute inset-y-0 left-1/2 w-px bg-white/40" />
            </div>

            <span
              className={`justify-self-end text-right font-mono tabular-nums transition-colors ${
                r.detected
                  ? `text-[12.5px] ${on ? "text-white" : "text-white/[.85]"}`
                  : `text-[10px] uppercase tracking-[0.1em] ${on ? "text-white/60" : "text-white/[.34]"}`
              }`}
            >
              {r.detected ? `${Math.round(r.pct)}%` : "none"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Two daily series on one index, 100 = each series' own baseline average, with
 * a dated marker and the gap between the lines shaded from the marker on.
 *
 * Built for the ChatGPT referral field note: tagged visits and the reads behind
 * them differ by an order of magnitude in raw counts, so plotting them raw would
 * flatten the one that matters. Indexing puts both on "share of normal", which
 * is the comparison the page is making. The axis starts at zero so a fall reads
 * at its true proportion.
 *
 * Interaction matches GrowthColumns: hover or focus a day to read both raw
 * values; at rest the readout shows the latest day. Lines draw in with a CSS
 * dash animation (no rAF, so it runs in every renderer) and skip it under
 * prefers-reduced-motion.
 */
export function IndexedLines({
  days,
  series,
  markerIndex,
  markerLabel,
  caption,
}: {
  days: string[];
  series: {
    label: string;
    color: string;
    values: number[];
    raw: number[];
  }[];
  markerIndex: number;
  markerLabel: string;
  caption?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const W = 440;
  const H = 210;
  const left = 26;
  const right = 8;
  const top = 22;
  const base = 182;
  const n = days.length;
  const plotW = W - left - right;
  const step = plotW / (n - 1);
  const top100 = Math.max(130, ...series.flatMap((s) => s.values)) + 4;
  const x = (i: number) => left + i * step;
  const y = (v: number) => base - (v / top100) * (base - top);

  const path = (vals: number[]) =>
    vals
      .map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
      .join(" ");

  /* The shaded gap from the marker to the end, between the first two series. */
  const [a, b] = series;
  const gap =
    a && b
      ? [
          ...a.values
            .slice(markerIndex)
            .map((v, k) => `${x(markerIndex + k)},${y(v)}`),
          ...b.values
            .slice(markerIndex)
            .map((v, k) => `${x(markerIndex + k)},${y(v)}`)
            .reverse(),
        ].join(" ")
      : "";

  const active = hover === null ? n - 1 : hover;
  const fmt = (v: number) => Math.round(v).toLocaleString("en-US");
  const vsBase = (v: number) => {
    const d = Math.round(v - 100);
    return d === 0 ? "at\u00a0normal" : `${d > 0 ? "+" : "−"}${Math.abs(d)}%`;
  };

  const ticks = [0, 7, markerIndex, n - 1].filter(
    (i, k, arr) => arr.indexOf(i) === k
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {series.map((s) => (
          <span
            key={s.label}
            className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-white/60"
          >
            <span
              aria-hidden="true"
              className="block h-[2px] w-4"
              style={{ background: s.color }}
            />
            {s.label}
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full touch-manipulation"
        role="img"
        aria-label={`Line chart, indexed to the average before ${markerLabel}. ${series
          .map(
            (s) =>
              `${s.label}: ${vsBase(s.values[0])} on ${days[0]}, ${vsBase(
                s.values[n - 1]
              )} on ${days[n - 1]}`
          )
          .join("; ")}.`}
        onMouseLeave={() => setHover(null)}
      >
        {/* Baseline at 100, and the floor. */}
        {[100, 50].map((g) => (
          <g key={g}>
            <line
              x1={left}
              x2={W - right}
              y1={y(g)}
              y2={y(g)}
              stroke={
                g === 100 ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)"
              }
              strokeDasharray={g === 100 ? "3 3" : undefined}
              strokeWidth="1"
            />
            <text
              x={left - 6}
              y={y(g) + 3}
              textAnchor="end"
              fontSize="9"
              fill="rgba(255,255,255,0.34)"
              className="font-mono"
            >
              {g}
            </text>
          </g>
        ))}
        <line
          x1={left}
          x2={W - right}
          y1={base}
          y2={base}
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1"
        />

        {gap && <polygon points={gap} fill="rgba(238,249,117,0.09)" />}

        {/* The marker. */}
        <line
          x1={x(markerIndex)}
          x2={x(markerIndex)}
          y1={top - 8}
          y2={base}
          stroke="rgba(238,249,117,0.55)"
          strokeWidth="1"
          strokeDasharray="2 3"
        />
        <text
          x={x(markerIndex) + 5}
          y={top - 1}
          fontSize="9.5"
          fill="#EEF975"
          className="font-mono"
        >
          {markerLabel}
        </text>

        {series.map((s, si) => (
          <path
            key={s.label}
            d={path(s.values)}
            fill="none"
            stroke={s.color}
            strokeWidth={si === 0 ? 1.9 : 1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            pathLength={1}
            className="lab-draw"
            style={{ animationDelay: `${si * 160}ms` }}
          />
        ))}

        {/* Hover hairline and points. */}
        <line
          x1={x(active)}
          x2={x(active)}
          y1={top}
          y2={base}
          stroke="rgba(255,255,255,0.25)"
          strokeWidth="1"
          opacity={hover === null ? 0 : 1}
        />
        {series.map((s) => (
          <circle
            key={s.label}
            cx={x(active)}
            cy={y(s.values[active])}
            r="2.6"
            fill={s.color}
          />
        ))}

        {/* Full-height hit targets, one per day. */}
        {days.map((d, i) => (
          <rect
            key={d}
            x={x(i) - step / 2}
            y={0}
            width={step}
            height={base}
            fill="transparent"
            tabIndex={0}
            role="button"
            aria-label={`${d}: ${series
              .map((s) => `${s.label} ${fmt(s.raw[i])}`)
              .join(", ")}`}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            className="cursor-default outline-none"
          />
        ))}

        {ticks.map((i) => (
          <text
            key={i}
            x={x(i)}
            y={H - 8}
            textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
            fontSize="9.5"
            fill={
              i === markerIndex
                ? "rgba(238,249,117,0.85)"
                : "rgba(255,255,255,0.4)"
            }
            className="font-mono"
          >
            {days[i]}
          </text>
        ))}
      </svg>

      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        {caption && (
          <span className="font-mono text-[11px] leading-[1.5] text-white/[.34]">
            {caption}
          </span>
        )}
        <span className="font-mono text-[11px] tabular-nums text-white/55">
          {days[active]}:{" "}
          {series
            .map(
              (s) =>
                `${s.label} ${fmt(s.raw[active])} (${vsBase(s.values[active])})`
            )
            .join(" · ")}
        </span>
      </div>
    </div>
  );
}
